import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import {
    Paper, Typography, Chip, Button, IconButton, Tooltip, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, Grow,
    TextField, Tabs, Tab, CircularProgress, TablePagination, InputAdornment,
} from '@material-ui/core'
import { Button as AntButton } from 'antd'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import LinearProgress from '@material-ui/core/LinearProgress'
import Skeleton from '@material-ui/lab/Skeleton'
import Autocomplete from '@material-ui/lab/Autocomplete'
import RefreshIcon from '@material-ui/icons/Refresh'
import PhoneCallbackIcon from '@material-ui/icons/PhoneCallback'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import ReportProblemIcon from '@material-ui/icons/ReportProblem'
import BlockIcon from '@material-ui/icons/Block'
import SkipNextIcon from '@material-ui/icons/SkipNext'
import NightsStayIcon from '@material-ui/icons/NightsStay'
import PhoneDisabledIcon from '@material-ui/icons/PhoneDisabled'
import ScheduleIcon from '@material-ui/icons/Schedule'
import PauseCircleOutlineIcon from '@material-ui/icons/PauseCircleOutline'
import VisibilityIcon from '@material-ui/icons/Visibility'
import DateRangeOutlinedIcon from '@material-ui/icons/DateRangeOutlined'
import PlaceOutlinedIcon from '@material-ui/icons/PlaceOutlined'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import TuneIcon from '@material-ui/icons/Tune'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded'
import moment from 'moment'
import * as XLSX from 'xlsx'
import HttpService from '../../services/httpService'
import { setSnackInfo } from '../../redux/actions/appAction'

const ORANGE = '#F47E44'
const ORANGE_DARK = '#c1531f'
const ORANGE_LIGHT = '#fff1ea'
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`
const INK = '#3d2b21'
const GREEN = '#1e7e34'
const GREEN_LIGHT = '#e6f4ea'
const RED = '#b3261e'
const RED_LIGHT = '#fdecea'
const AMBER = '#b26a00'
const AMBER_LIGHT = '#fff3e0'
const BLUE = '#1b5e9e'
const BLUE_LIGHT = '#e8f1fa'
const GREY = '#5f6b76'
const GREY_LIGHT = '#eef1f4'
// The Move to Marketing — AI Review download button, to the value. Kept apart
// from GREEN above, which is the "Success" outcome tone and must not drift
// with it: one means "this call worked", the other means "this button exports".
const EXPORT_GREEN = '#1e8449'
const EXPORT_GREEN_GRADIENT = `linear-gradient(135deg, #146c43, ${EXPORT_GREEN})`

// One definition of what each outcome means, shared by the tiles, the run
// rows and the per-lead details - so the same word never means two things in
// two places on the same screen.
const OUTCOME = {
    triggered: { label: 'Success', color: GREEN, bg: GREEN_LIGHT, Icon: CheckCircleIcon, hint: 'The AI call was placed' },
    failed: { label: 'Failed', color: RED, bg: RED_LIGHT, Icon: ErrorOutlineIcon, hint: 'No call could be placed — will retry next run' },
    skipped: { label: 'Skipped', color: GREY, bg: GREY_LIGHT, Icon: SkipNextIcon, hint: 'Claimed by another run already in progress' },
    gaveUp: { label: 'Gave up', color: AMBER, bg: AMBER_LIGHT, Icon: ReportProblemIcon, hint: 'Failed 3 times — no longer retried automatically' },
    movedToJunk: { label: 'Moved to Junk', color: AMBER, bg: AMBER_LIGHT, Icon: BlockIcon, hint: 'Number is not dialable — junked and sent to Marketing' },
    noPhone: { label: 'No phone', color: GREY, bg: GREY_LIGHT, Icon: PhoneDisabledIcon, hint: 'No number stored on the customer' },
    outsideWindow: { label: 'Deferred', color: BLUE, bg: BLUE_LIGHT, Icon: NightsStayIcon, hint: 'Ran outside 9AM–9PM IST — nothing was called' },
    error: { label: 'Error', color: RED, bg: RED_LIGHT, Icon: ErrorOutlineIcon, hint: 'The record itself could not be processed' },
    misconfigured: { label: 'Misconfigured', color: RED, bg: RED_LIGHT, Icon: ReportProblemIcon, hint: 'Hold campaign/agent not set on the server' },
    deferred: { label: 'Deferred', color: BLUE, bg: BLUE_LIGHT, Icon: NightsStayIcon, hint: 'Outside the calling window' },
}

const RUN_STATUS = {
    completed: { label: 'Completed', color: GREEN, bg: GREEN_LIGHT },
    processing: { label: 'Running', color: BLUE, bg: BLUE_LIGHT },
    failed: { label: 'Failed', color: RED, bg: RED_LIGHT },
}

// The bar under each run row: proportions of what that run actually did.
const BAR_PARTS = [
    { key: 'triggered', color: GREEN },
    { key: 'failed', color: RED },
    { key: 'movedToJunk', color: AMBER },
    { key: 'noPhone', color: '#9aa6b1' },
    { key: 'skipped', color: GREY },
]

const BrandLinearProgress = withStyles(() => ({
    root: { height: 4, borderRadius: 999, backgroundColor: 'rgba(193,83,31,0.10)' },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress)

const useStyles = makeStyles((theme) => ({
    root: { padding: '4px 4px 32px' },
    headerBanner: {
        display: 'flex', alignItems: 'center', gap: 16,
        background: HEADER_GRADIENT,
        borderRadius: 16, padding: '16px 26px', marginBottom: 16, color: '#fff',
        boxShadow: '0 8px 24px rgba(93,55,28,0.18)',
        [theme.breakpoints.down('xs')]: { padding: '16px 18px', flexWrap: 'wrap' },
    },
    headerIconWrap: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 46, height: 46, borderRadius: 14, flexShrink: 0,
        background: 'rgba(255,255,255,0.18)',
    },
    headerTitle: { fontSize: 20, fontWeight: 700, color: '#fff', lineHeight: 1.3 },
    headerSubtitle: { fontSize: 13, color: 'rgba(255,255,255,0.85)', marginTop: 4, maxWidth: 740 },
    headerActions: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },

    // ---- header range chip + refresh ---------------------------------------
    // Lifted from the Move to Marketing — AI Review dashboard so the two read
    // as one product. Same chip, same labelled button, same "Updated 4m ago".
    headerDateChip: {
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '6px 13px', borderRadius: 20,
        // Darkened, not frosted: on the light end of the gradient a
        // translucent white panel drops below AA.
        background: 'rgba(0,0,0,0.26)', color: '#fff',
        fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0,
    },
    '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
    '@keyframes pulseDot': {
        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
        '50%': { opacity: 0.45, transform: 'scale(0.82)' },
    },
    // Solid white with the word "Refresh" on it. The old icon-only button used
    // rgba(255,255,255,0.18), which all but vanished against #ff9c66.
    refreshButton: {
        display: 'inline-flex', alignItems: 'center', gap: 7,
        padding: '8px 16px', borderRadius: 999, border: 'none',
        background: '#fff', color: ORANGE_DARK,
        fontSize: 12.5, fontWeight: 800, letterSpacing: 0.2, fontFamily: 'inherit',
        cursor: 'pointer', whiteSpace: 'nowrap',
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
        '&:hover': { background: '#fff6f1' },
        '&:disabled': { cursor: 'default', opacity: 0.75 },
    },
    refreshSpinning: { animation: '$spin 0.9s linear infinite' },
    lastUpdatedText: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 11, fontWeight: 600, color: '#fff',
        background: 'rgba(0,0,0,0.42)', padding: '3px 10px',
        borderRadius: 999, whiteSpace: 'nowrap',
    },
    liveDot: {
        width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
        background: '#FFD2B5', animation: '$pulseDot 2s ease-in-out infinite',
    },

    // ---- filter card --------------------------------------------------------
    filterPaper: {
        padding: '16px 20px', marginBottom: 14, borderRadius: 16,
        border: '1px solid #eaeff6', boxShadow: '0 2px 16px rgba(28,55,93,0.06)',
    },
    filterHeaderRow: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 8, marginBottom: 14,
    },
    filterHeader: {
        display: 'flex', alignItems: 'center', gap: 8,
        color: ORANGE_DARK, fontWeight: 600, fontSize: 15,
    },
    filterLoadingBadge: {
        display: 'flex', alignItems: 'center', gap: 7,
        padding: '4px 12px', borderRadius: 999,
        background: ORANGE_LIGHT, color: ORANGE_DARK, fontSize: 12, fontWeight: 700,
    },
    // The two dates are ONE range, so they sit in a shared bordered group with
    // the arrow between them rather than as two unrelated boxes.
    dateGroup: {
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 12px 7px 14px', borderRadius: 12,
        border: '1px solid #dfe5ef', background: '#fff', flexShrink: 0,
        cursor: 'pointer',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        '&:hover': { borderColor: '#c3ccd9' },
        '&:focus-within': { borderColor: ORANGE, boxShadow: `0 0 0 3px ${ORANGE}1f` },
    },
    dateGroupLabel: {
        fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6,
        textTransform: 'uppercase', color: '#9aa4b4', whiteSpace: 'nowrap',
    },
    // Borderless: the GROUP carries the border, so an outlined box inside it
    // would be a border inside a border.
    dateInput: {
        width: 124,
        '& input': { fontSize: 13, fontWeight: 600, color: INK, padding: '5px 0', cursor: 'pointer' },
        '& .MuiInput-underline:before, & .MuiInput-underline:after': { display: 'none' },
    },
    dateDash: { color: '#c3ccd9', fontSize: 13, fontWeight: 700, userSelect: 'none' },
    pickerField: { width: 190, flexShrink: 0 },
    centreField: { width: 226, flexShrink: 0 },
    filterActions: { display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', flexShrink: 0 },
    '@media (max-width: 1099.95px)': {
        filterActions: { marginLeft: 0, width: '100%', justifyContent: 'flex-end' },
    },
    // Same pill as the Move to Marketing review, so a lead number looks the
    // same wherever it appears.
    customerCell: { display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 },
    avatar: {
        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
        display: 'grid', placeItems: 'center',
        fontSize: 11, fontWeight: 800, letterSpacing: 0.2,
    },
    customerName: {
        fontSize: 13, fontWeight: 600, color: INK,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    },
    // #756458, not the #8b7a6f used elsewhere here: that one measures 3.72 on
    // the row's own hover tint and misses AA for normal text.
    customerStage: {
        fontSize: 11, color: '#756458', marginTop: 1,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    },
    leadLink: {
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '3px 10px', borderRadius: 7,
        background: '#EAF2FD', color: BLUE,
        fontWeight: 700, fontSize: 12.5, cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        '&:hover': { background: '#d7e6fa' },
    },
    // ---- "you changed something, press Apply" -------------------------------
    dirtyBadge: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 12px', borderRadius: 999,
        background: '#fff4e8', border: `1px solid ${ORANGE}66`,
        color: '#9c4a16', fontSize: 11.5, fontWeight: 800, whiteSpace: 'nowrap',
    },
    dirtyDot: {
        width: 6, height: 6, borderRadius: '50%', background: ORANGE_DARK,
        animation: '$pulseDot 1.6s ease-in-out infinite',
    },
    // Draws the eye to the one control that acts on the change.
    applyButtonDirty: { animation: '$nudge 1.8s ease-in-out infinite' },
    '@keyframes nudge': {
        '0%, 100%': { boxShadow: '0 4px 10px rgba(193,83,31,0.32)' },
        '50%': { boxShadow: `0 4px 10px rgba(193,83,31,0.32), 0 0 0 6px ${ORANGE}26` },
    },
    applyHint: {
        display: 'flex', alignItems: 'center', gap: 6,
        marginTop: 12, minHeight: 16,
        fontSize: 11.5, fontWeight: 700, color: '#9c4a16',
    },
    pagination: {
        borderTop: '1px solid #eceff3',
        '& .MuiTablePagination-toolbar': { minHeight: 44 },
        '& .MuiTablePagination-caption': { fontSize: 12, color: '#5f574f' },
    },
    filterRow: {
        display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16,
        transition: 'opacity 0.2s ease',
    },
    field: {
        '& .MuiOutlinedInput-root': { borderRadius: 10 },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE },
        '& .MuiInputLabel-root.Mui-focused': { color: ORANGE_DARK },
    },
    applyButton: {
        borderRadius: '20px !important', fontWeight: 600,
        display: 'inline-flex !important', alignItems: 'center !important',
        color: '#fff !important',
        background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE}) !important`,
        borderColor: 'transparent !important',
        boxShadow: '0 4px 10px rgba(193,83,31,0.32) !important',
    },
    clearButton: {
        borderRadius: '20px !important', fontWeight: 500,
        display: 'inline-flex !important', alignItems: 'center !important',
    },
    quickRanges: { display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginTop: 12 },
    quickLabel: { fontSize: 11.5, fontWeight: 700, color: '#8b7a6f', textTransform: 'uppercase', letterSpacing: 0.4 },
    quickChip: {
        fontSize: 11.5, fontWeight: 700, height: 24, cursor: 'pointer',
        background: '#f2f4f8', color: '#5b4b42',
        '&:hover': { background: ORANGE_LIGHT, color: ORANGE_DARK },
    },
    quickChipOn: { background: ORANGE_LIGHT, color: ORANGE_DARK, border: `1px solid ${ORANGE}66` },

    progressSlot: { height: 4, marginBottom: 10 },
    loadingBanner: {
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 14px', marginBottom: 14, borderRadius: 12,
        background: ORANGE_LIGHT, border: `1px solid ${ORANGE}33`,
    },
    loadingBannerTitle: { fontSize: 13, fontWeight: 700, color: ORANGE_DARK },
    loadingBannerHint: { fontSize: 11.5, color: '#7a6a60', marginTop: 1 },

    card: {
        borderRadius: 16, border: '1px solid #eceff3',
        boxShadow: '0 4px 24px rgba(93,55,28,0.08)',
        padding: '18px 20px', marginBottom: 18,
    },
    sectionLabel: {
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 13, fontWeight: 700, color: ORANGE_DARK,
        textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 14,
    },
    sectionNote: { fontSize: 11.5, color: '#8b7a6f', textTransform: 'none', letterSpacing: 0, fontWeight: 500, marginLeft: 'auto' },

    tileGrid: {
        display: 'grid', gap: 12,
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    },
    tile: {
        borderRadius: 14, padding: '14px 16px', border: '1px solid transparent',
        transition: 'transform 140ms ease, box-shadow 140ms ease',
    },
    tileClickable: {
        cursor: 'pointer',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 18px rgba(93,55,28,0.12)' },
    },
    tileTop: { display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 },
    tileLabel: { fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 },
    tileValue: { fontSize: 27, fontWeight: 700, lineHeight: 1.05, color: INK },
    tileHint: { fontSize: 11, color: '#8b7a6f', marginTop: 4, lineHeight: 1.45 },

    tableWrap: { borderRadius: 12, border: '1px solid #eceff3', overflow: 'hidden' },
    // `tableWrap` hides its overflow to keep the rounded corners clean, which
    // also meant a capped TableContainer could not scroll - the rows below the
    // cap were simply clipped. Anything with a maxHeight uses this instead.
    scrollWrap: { borderRadius: 12, border: '1px solid #eceff3', overflow: 'auto' },
    headCell: {
        backgroundColor: ORANGE_DARK, color: '#fff', fontWeight: 700,
        fontSize: 12.5, letterSpacing: 0.3, borderBottom: 'none', whiteSpace: 'nowrap',
    },
    bodyCell: { fontSize: 13.5, color: INK },
    row: {
        transition: 'background-color 150ms ease',
        '&:nth-of-type(even)': { backgroundColor: '#fafbfd' },
        '&:hover': { backgroundColor: ORANGE_LIGHT },
    },
    mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
    emptyState: { textAlign: 'center', padding: '34px 16px', color: '#8b7a6f' },

    countStrip: { display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' },
    countPill: {
        fontSize: 11.5, fontWeight: 700, borderRadius: 7, padding: '2px 8px',
        display: 'inline-flex', alignItems: 'center', gap: 4,
    },

    bar: { display: 'flex', height: 5, borderRadius: 999, overflow: 'hidden', background: '#eef1f4', marginTop: 7, minWidth: 110 },

    whenMain: { fontSize: 13.5, fontWeight: 600, color: INK },
    whenSub: { fontSize: 11, color: '#8b7a6f' },
    cityLine: {
        display: 'flex', alignItems: 'center', gap: 3,
        fontSize: 11, color: '#8b7a6f', marginTop: 2,
    },

    tabs: { minHeight: 36, marginBottom: 12 },
    tab: { minHeight: 36, textTransform: 'none', fontWeight: 700, fontSize: 13 },

    // ---- leads toolbar ------------------------------------------------------
    // The card title, the search box and the export sit on one line, so the
    // two things you do to this table are where the table starts rather than
    // scattered up in the page header.
    leadsToolbar: {
        display: 'flex', alignItems: 'center', gap: 10,
        flexWrap: 'wrap', marginBottom: 12,
    },
    leadsToolbarActions: {
        display: 'flex', alignItems: 'center', gap: 8,
        marginLeft: 'auto', flexShrink: 0,
    },
    '@media (max-width: 719.95px)': {
        leadsToolbarActions: { marginLeft: 0, width: '100%' },
        searchField: { flex: '1 1 auto', width: 'auto !important' },
    },
    searchField: {
        width: 268,
        '& .MuiOutlinedInput-root': { borderRadius: 999, background: '#fff' },
        '& .MuiOutlinedInput-input': { fontSize: 13, padding: '8.5px 0' },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE },
    },
    exportButton: {
        textTransform: 'none', fontWeight: 700, fontSize: 12.5,
        borderRadius: 999, padding: '6px 16px', whiteSpace: 'nowrap',
        color: '#fff', background: EXPORT_GREEN_GRADIENT,
        boxShadow: '0 3px 9px rgba(30,132,73,0.28)',
        '&:hover': {
            background: 'linear-gradient(135deg, #0f5132, #146c43)',
            boxShadow: '0 6px 16px rgba(30,132,73,0.35)',
        },
        '&.Mui-disabled': { background: '#e8ebef', color: '#9aa6b1', boxShadow: 'none' },
    },
    // Sits between the tabs and the table: says what the table is currently
    // showing, so a short list after a search never reads as a short bucket.
    resultMeta: {
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        minHeight: 22, marginBottom: 8,
        fontSize: 12, fontWeight: 600, color: '#8b7a6f',
    },
    matchPill: {
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '2px 10px', borderRadius: 999,
        background: ORANGE_LIGHT, color: ORANGE_DARK, fontSize: 11.5, fontWeight: 800,
    },
    clearSearchLink: {
        background: 'none', border: 'none', padding: 0, font: 'inherit',
        color: BLUE, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline',
    },
    capNote: { fontSize: 11.5, color: '#a79d96', fontWeight: 600 },

    dialogPaper: { borderRadius: 16, maxWidth: 820, width: '100%' },
    dialogTitle: { fontSize: 17, fontWeight: 700, color: INK, paddingBottom: 4 },
    errorBox: {
        background: RED_LIGHT, border: `1px solid ${RED}22`, borderRadius: 10,
        padding: '10px 14px', fontSize: 12.5, color: RED, marginBottom: 14, lineHeight: 1.55,
    },
}))

const DEFAULT_FILTERS = {
    startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    // Objects, not ids: the Autocomplete needs the whole option to render its
    // chip. The ids are taken on the way out to the server.
    city: [],
    experienceCenterId: [],
}

// A stable signature of a filter set. Ids are sorted, so re-picking the same
// two cities in a different order is not treated as a change.
const filterKey = (f) => JSON.stringify({
    startDate: f.startDate,
    endDate: f.endDate,
    city: f.city.map((c) => c._id).sort(),
    experienceCenterId: f.experienceCenterId.map((e) => e._id).sort(),
})

// The ids the two endpoints actually want.
const scopeIds = (f) => ({
    city: f.city.map((c) => c._id),
    experienceCenterId: f.experienceCenterId.map((e) => e._id),
})

// An experience centre's location, whether it arrived populated or as a bare id.
const locationIdOf = (ec) => String((ec && ec.locationId && ec.locationId._id) || (ec && ec.locationId) || '')

// The presets the old "Last 7 / 30 / 90 days" dropdown offered, kept as one
// click each so nothing got slower in exchange for the range being precise.
const QUICK_RANGES = [
    { label: 'Today', days: 0 },
    { label: '7 days', days: 6 },
    { label: '30 days', days: 29 },
    { label: '90 days', days: 89 },
]

const LEAD_COLUMNS = 7
const LEAD_ROWS_OPTIONS = [10, 25, 50, 100]

const BUCKETS = [
    { value: 'due', label: 'Due now', countKey: 'dueNow' },
    { value: 'upcoming', label: 'Upcoming', countKey: 'upcoming' },
    { value: 'called', label: 'Called', countKey: 'called' },
    { value: 'gaveUp', label: 'Gave up', countKey: 'gaveUp' },
]

// Columns of the Leads sheet, in order. Declared once so the sheet, its
// column widths and the header row cannot drift apart.
const LEAD_EXPORT_HEADER = [
    'Sl. No.', 'Lead No', 'Customer', 'Current Stage', 'Experience Centre',
    'City', 'Revoke Date', 'Called At', 'Attempts', 'Lead Link',
]
const LEAD_EXPORT_WIDTHS = [8, 12, 26, 24, 24, 16, 14, 18, 10, 46]

// Characters Excel forbids in a sheet name, plus its 31-char cap.
const safeSheetName = (name) => String(name).replace(/[\\/?*[\]:]/g, '-').slice(0, 31)

const rangeOf = (days) => ({
    startDate: moment().subtract(days, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
})

// Initials avatars. The tone is picked by a hash of the name, so the same
// customer keeps the same colour on every render instead of flickering, and
// two adjacent rows rarely collide. Every pair below clears AA for 11px bold
// on its own tint (4.77 at worst).
const AVATAR_TONES = [
    { bg: '#eef3fc', fg: '#2c5fb8' },
    { bg: '#e8f6ee', fg: '#1f7a4d' },
    { bg: '#fff1ea', fg: '#a8450f' },
    { bg: '#f3eefc', fg: '#5b3fa8' },
    { bg: '#e8f4f6', fg: '#13697a' },
    { bg: '#fdf0f0', fg: '#a3312a' },
]
const NO_NAME_TONE = { bg: '#f1f3f5', fg: '#55606b' }

const avatarToneOf = (name) => {
    if (!name) return NO_NAME_TONE
    let h = 0
    for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) % 997
    return AVATAR_TONES[h % AVATAR_TONES.length]
}

// First letters of the first two words - "Rahul Kumar Sharma" is RK, not RKS.
const initialsOf = (name) => String(name || '')
    .trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0].toUpperCase()).join('') || '?'

const fmtDuration = (ms) => {
    if (!ms && ms !== 0) return '—'
    if (ms < 1000) return `${ms} ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`
    return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`
}

const HoldRevokeMonitor = (props) => {
    const classes = useStyles()

    const [filters, setFilters] = useState(DEFAULT_FILTERS)
    // The range the figures on screen were actually fetched with. Editing a
    // date must not move the header chip until Apply has run, or the banner
    // describes a window the numbers do not cover.
    const [appliedRange, setAppliedRange] = useState(null)
    const [appliedScopeLabel, setAppliedScopeLabel] = useState({ cities: [], centres: [] })
    const [lastUpdated, setLastUpdated] = useState(null)
    // "Updated 4 minutes ago" is only true at the instant it renders, so a slow
    // tick re-renders it. 30s is plenty - moment rounds to the minute anyway.
    const [nowTick, setNowTick] = useState(Date.now())
    useEffect(() => {
        const id = setInterval(() => setNowTick(Date.now()), 30000)
        return () => clearInterval(id)
    }, [])

    const [loading, setLoading] = useState(true)
    const [overview, setOverview] = useState(null)

    const [bucket, setBucket] = useState('due')
    const [leads, setLeads] = useState([])
    const [leadsTotal, setLeadsTotal] = useState(0)
    const [leadsLoading, setLeadsLoading] = useState(false)
    const [leadPage, setLeadPage] = useState(0)
    const [leadRowsPerPage, setLeadRowsPerPage] = useState(25)
    // Lead no / customer name search. Filtered in the browser: /leads already
    // returns the whole capped window in one request, so there is nothing to
    // ask the server for and the list narrows as you type.
    const [leadSearch, setLeadSearch] = useState('')
    const [exporting, setExporting] = useState(false)

    // City / experience-centre options. Loaded once; the centre list is
    // narrowed from them rather than refetched per city - /experience-center
    // already populates locationId and caches, so the dependency is free and
    // cannot fall out of step with what is on screen.
    const [cities, setCities] = useState([])
    const [allCentres, setAllCentres] = useState([])
    const [optionsLoading, setOptionsLoading] = useState(true)
    // The filters the data on screen was fetched with. A ref, because the
    // bucket switch and the 5s poll both need them without re-rendering.
    const appliedFilters = useRef(DEFAULT_FILTERS)
    // The same thing as a comparable string, for the "not applied yet" state.
    // This one IS state - it has to drive a render.
    const [appliedKey, setAppliedKey] = useState(filterKey(DEFAULT_FILTERS))

    const [openRun, setOpenRun] = useState(null)
    const [runLoading, setRunLoading] = useState(false)

    // `override` lets Clear fetch with filters state has not committed yet.
    // Never passed straight to onClick - the click event would arrive as the
    // silent flag.
    const loadOverview = useCallback(async (silent, override) => {
        const active = override || filters
        if (!silent) setLoading(true)
        try {
            const res = await HttpService.getHoldRevokeOverview({ ...active, ...scopeIds(active) })
            setOverview(res.data)
            setLastUpdated(new Date())
            setAppliedRange({ startDate: active.startDate, endDate: active.endDate })
            appliedFilters.current = active
            setAppliedKey(filterKey(active))
            setAppliedScopeLabel({
                cities: active.city.map((c) => c.name).filter(Boolean),
                centres: active.experienceCenterId.map((e) => e.name || e.code).filter(Boolean),
            })
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? 'Could not load the hold revoke dashboard', 'error')
        }
        setLoading(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters])

    // `scope` is passed in rather than read from `filters`, so Apply can fetch
    // with a selection state has not committed yet.
    const loadLeads = useCallback(async (which, scope) => {
        setLeadsLoading(true)
        try {
            const res = await HttpService.getHoldRevokeLeads(which, 500, scope || scopeIds(filters))
            setLeads(res.data.leads || [])
            setLeadsTotal(Number(res.data.totalCount) || (res.data.leads || []).length)
            setLeadPage(0)
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? 'Could not load the leads', 'error')
        }
        setLeadsLoading(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters])

    const loadOptions = useCallback(async () => {
        setOptionsLoading(true)
        // Settled, not all: one list failing should not leave the other empty.
        // These endpoints answer 404 when their collection is empty and axios
        // rejects on that, so an empty result is normalised rather than
        // reported as a failure.
        const [locRes, ecRes] = await Promise.allSettled([
            HttpService.getLocation(),
            HttpService.getExperinceCenter(),
        ])
        const listOf = (settled) => {
            if (settled.status === 'fulfilled') return Array.isArray(settled.value?.data) ? settled.value.data : []
            if (settled.reason?.response?.status === 404) return []
            return null
        }
        const locList = listOf(locRes)
        if (locList) setCities(locList)
        else props.setSnackInfo('Could not load the city list', 'error')
        const ecList = listOf(ecRes)
        if (ecList) setAllCentres(ecList)
        else props.setSnackInfo('Could not load the experience centre list', 'error')
        setOptionsLoading(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Mount only. Changing a filter must NOT refetch on its own - Apply and
    // Refresh are what run it, so the controls on screen and the numbers under
    // them stay in step.
    useEffect(() => {
        loadOptions()
        loadOverview()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    // Switching bucket refetches with whatever scope is already APPLIED, not
    // whatever is half-typed in the filter card.
    useEffect(() => {
        loadLeads(bucket, scopeIds(appliedFilters.current))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bucket])

    // A run in progress changes under the user, so the page follows it until
    // it settles rather than leaving a stale "Running" on screen.
    const hasRunning = useMemo(
        () => (overview?.runs || []).some((r) => r.status === 'processing'),
        [overview],
    )
    useEffect(() => {
        if (!hasRunning) return undefined
        // appliedFilters, not the live ones: loadOverview closes over
        // `filters`, so an unapplied edit would have been quietly fetched by
        // the next tick - changing the numbers without the user pressing
        // anything, and clearing the "not applied yet" hint while it did.
        const t = setInterval(() => loadOverview(true, appliedFilters.current), 5000)
        return () => clearInterval(t)
    }, [hasRunning, loadOverview])

    const openRunDetails = async (run) => {
        setRunLoading(true)
        setOpenRun({ _id: run._id, loading: true })
        try {
            const res = await HttpService.getHoldRevokeRun(run._id)
            setOpenRun(res.data.run)
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? 'Could not load that run', 'error')
            setOpenRun(null)
        }
        setRunLoading(false)
    }

    // Native date inputs only open their calendar from a ~20px icon. These
    // make the whole group the trigger.
    const startDateRef = useRef(null)
    const endDateRef = useRef(null)
    const openPicker = (ref) => {
        const input = ref.current
        if (!input || input.disabled) return
        input.focus()
        // showPicker arrived in Chrome/Edge 99, is absent in Safari and throws
        // outside a user gesture - focus above is the graceful fallback.
        try {
            if (typeof input.showPicker === 'function') input.showPicker()
        } catch (e) { /* focus already happened */ }
    }

    // The centre list narrows to the chosen cities. Filtered from the list
    // already in hand rather than refetched per city.
    const visibleCentres = filters.city.length
        ? allCentres.filter((ec) => filters.city.some((c) => String(c._id) === locationIdOf(ec)))
        : allCentres

    const handleCityChange = (event, newCities) => {
        // Drop any centre that no longer sits in the chosen cities - keeping
        // one would scope the data to a centre the list no longer offers.
        const keptCentres = newCities.length
            ? filters.experienceCenterId.filter((ec) =>
                newCities.some((c) => String(c._id) === locationIdOf(ec)))
            : filters.experienceCenterId
        setFilters({ ...filters, city: newCities, experienceCenterId: keptCentres })
    }

    // One Apply for the whole card: the tabs read their counts from the
    // overview and the table from /leads, so refreshing one without the other
    // would leave a tab labelled "Due now (12)" above a table of 40.
    const applyFilters = () => {
        loadOverview()
        loadLeads(bucket, scopeIds(filters))
    }

    const clearFilters = () => {
        setFilters(DEFAULT_FILTERS)
        // Clear applies itself - it means "show me everything", and leaving it
        // to prompt for Apply would make it two clicks to undo one. The
        // defaults are passed explicitly because `filters` in this closure is
        // still the pre-Clear value.
        loadOverview(false, DEFAULT_FILTERS)
        loadLeads(bucket, scopeIds(DEFAULT_FILTERS))
    }

    // Paged in the browser: /leads already returns the whole capped window in
    // one request, so paging here costs nothing and keeps the control pinned
    // under the table.
    // The controls no longer describe what is on screen.
    const isDirty = appliedKey !== filterKey(filters)

    // "Bangalore · 2 centres", or the single name when there is one of it - a
    // chip reading "1 city" tells the reader nothing they wanted.
    const scopeLabel = (() => {
        const parts = []
        const { cities: cs = [], centres = [] } = appliedScopeLabel
        if (cs.length === 1) parts.push(cs[0])
        else if (cs.length > 1) parts.push(`${cs.length} cities`)
        if (centres.length === 1) parts.push(centres[0])
        else if (centres.length > 1) parts.push(`${centres.length} centres`)
        return parts.join(' · ')
    })()
    const scopeTitle = [
        appliedScopeLabel.cities.length ? `Cities: ${appliedScopeLabel.cities.join(', ')}` : '',
        appliedScopeLabel.centres.length ? `Centres: ${appliedScopeLabel.centres.join(', ')}` : '',
    ].filter(Boolean).join(' — ')

    // Lead no OR customer name, case-insensitive, substring - "12" finds
    // lead 11234 as well as 12345, which is what someone squinting at a
    // half-remembered number actually wants.
    const leadQuery = leadSearch.trim().toLowerCase()
    const filteredLeads = useMemo(() => {
        if (!leadQuery) return leads
        return leads.filter((l) => String(l.leadNo ?? '').toLowerCase().includes(leadQuery)
            || String(l.customerName ?? '').toLowerCase().includes(leadQuery))
    }, [leads, leadQuery])

    // Typing can shrink the list under the current page. Clamping here rather
    // than resetting to page 1 on every keystroke keeps your place when the
    // match count is still comfortably above where you are.
    const leadPageCount = Math.max(1, Math.ceil(filteredLeads.length / leadRowsPerPage))
    const safeLeadPage = Math.min(leadPage, leadPageCount - 1)
    const pagedLeads = filteredLeads.slice(
        safeLeadPage * leadRowsPerPage,
        safeLeadPage * leadRowsPerPage + leadRowsPerPage,
    )

    const p = overview?.pipeline || {}
    const t = overview?.totals || {}

    const outcomeTiles = ['triggered', 'failed', 'skipped', 'movedToJunk', 'noPhone', 'outsideWindow']

    // The applied range, written the way people read it: a range inside one
    // month collapses rather than repeating the month, and a whole calendar
    // month is just its name.
    const range = appliedRange || filters
    const rangeLabel = (() => {
        if (!range.startDate || !range.endDate) return 'All dates'
        const from = moment(range.startDate)
        const to = moment(range.endDate)
        if (!from.isValid() || !to.isValid()) return 'All dates'
        const wholeMonth = from.isSame(to, 'month')
            && from.isSame(from.clone().startOf('month'), 'day')
            && to.isSame(to.clone().endOf('month'), 'day')
        if (wholeMonth) return from.format('MMMM YYYY')
        if (from.isSame(to, 'day')) return from.format('DD MMM YYYY')
        if (from.isSame(to, 'month')) return `${from.format('DD')} — ${to.format('DD MMM YYYY')}`
        if (from.isSame(to, 'year')) return `${from.format('DD MMM')} — ${to.format('DD MMM YYYY')}`
        return `${from.format('DD MMM YYYY')} — ${to.format('DD MMM YYYY')}`
    })()
    const rangeDays = (() => {
        if (!range.startDate || !range.endDate) return null
        const n = moment(range.endDate).diff(moment(range.startDate), 'days') + 1
        return n > 0 ? n : null
    })()

    // Everything on screen, as one workbook: the tiles, the run table and the
    // lead table the reader is looking at - narrowed by the same search, so
    // the file matches the screen rather than quietly holding more.
    const downloadExcel = () => {
        setExporting(true)
        try {
            const wb = XLSX.utils.book_new()
            const bucketLabel = (BUCKETS.find((b) => b.value === bucket) || {}).label || bucket

            // --- Summary ---------------------------------------------------
            const summaryRows = [
                { Metric: 'Period', Value: rangeLabel },
                { Metric: 'From', Value: range.startDate || '—' },
                { Metric: 'To', Value: range.endDate || '—' },
                { Metric: 'Cities', Value: appliedScopeLabel.cities.join(', ') || 'All cities' },
                { Metric: 'Experience Centres', Value: appliedScopeLabel.centres.join(', ') || 'All centres' },
                { Metric: 'Downloaded at', Value: moment().format('DD MMM YYYY, hh:mm A') },
                { Metric: '', Value: '' },
                { Metric: 'Runs in period', Value: t.runs ?? 0 },
                { Metric: 'Failed runs', Value: t.failedRuns ?? 0 },
                { Metric: '', Value: '' },
                ...outcomeTiles.map((k) => ({ Metric: OUTCOME[k].label, Value: t[k] ?? 0 })),
                { Metric: '', Value: '' },
                ...BUCKETS.map((b) => ({ Metric: `Pipeline — ${b.label}`, Value: p[b.countKey] ?? 0 })),
            ]
            const summarySheet = XLSX.utils.json_to_sheet(summaryRows, { header: ['Metric', 'Value'] })
            summarySheet['!cols'] = [{ wch: 26 }, { wch: 44 }]
            XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary')

            // --- Runs ------------------------------------------------------
            const runRows = (overview?.runs || []).map((run) => ({
                'When': moment(run.createdAt).format('DD MMM YYYY, HH:mm'),
                'Status': (RUN_STATUS[run.status] || {}).label || run.status || '—',
                'Found': run.found ?? 0,
                'Success': run.triggered ?? 0,
                'Failed': run.failed ?? 0,
                'Skipped': run.skipped ?? 0,
                'Gave up': run.gaveUp ?? 0,
                'Moved to Junk': run.movedToJunk ?? 0,
                'No phone': run.noPhone ?? 0,
                'Deferred': run.outsideWindow ?? 0,
                'Duration': fmtDuration(run.durationMs),
                'Triggered by': run.triggeredBy || '—',
                'Error': run.errorMessage || '',
            }))
            const runHeader = ['When', 'Status', 'Found', 'Success', 'Failed', 'Skipped', 'Gave up',
                'Moved to Junk', 'No phone', 'Deferred', 'Duration', 'Triggered by', 'Error']
            const runSheet = XLSX.utils.json_to_sheet(runRows, { header: runHeader })
            runSheet['!cols'] = [22, 12, 8, 10, 9, 10, 10, 15, 11, 11, 12, 16, 40].map((wch) => ({ wch }))
            XLSX.utils.book_append_sheet(wb, runSheet, 'Runs')

            // --- Leads -----------------------------------------------------
            const leadRows = filteredLeads.map((l, i) => ({
                'Sl. No.': i + 1,
                'Lead No': l.leadNo || '',
                'Customer': l.customerName || '',
                'Current Stage': l.currentStage || '',
                'Experience Centre': l.experienceCenter || '',
                'City': l.city || '',
                // Dates go out as text in the format the table shows. Excel
                // reads a bare DD-MM date as US MM-DD and silently reorders it.
                'Revoke Date': l.holdRevokeDate ? moment(l.holdRevokeDate).format('DD MMM YYYY') : '',
                'Called At': l.holdRevokeCalledAt ? moment(l.holdRevokeCalledAt).format('DD MMM YYYY, HH:mm') : '',
                'Attempts': l.attempts ?? 0,
                'Lead Link': l._id ? `${window.location.origin}/project-logs/${l._id}` : '',
            }))
            const leadSheet = XLSX.utils.json_to_sheet(leadRows, { header: LEAD_EXPORT_HEADER })
            leadSheet['!cols'] = LEAD_EXPORT_WIDTHS.map((wch) => ({ wch }))
            XLSX.utils.book_append_sheet(wb, leadSheet, safeSheetName(`Leads - ${bucketLabel}`))

            const stamp = (range.startDate && range.endDate)
                ? `${range.startDate}_to_${range.endDate}`
                : moment().format('YYYY-MM-DD')
            XLSX.writeFile(wb, `HoldRevoke_${bucketLabel.replace(/\s+/g, '')}_${stamp}.xlsx`)
            props.setSnackInfo(
                `Downloaded ${leadRows.length} lead${leadRows.length === 1 ? '' : 's'} and ${runRows.length} run${runRows.length === 1 ? '' : 's'}`,
                'success',
            )
        } catch (error) {
            props.setSnackInfo(error?.message || 'Could not build the Excel file', 'error')
        }
        setExporting(false)
    }

    // Both ends must be real and the right way round before the server is
    // asked - a 400 is a worse answer than a disabled button.
    const datesValid = Boolean(filters.startDate) && Boolean(filters.endDate)
        && moment(filters.startDate).isValid() && moment(filters.endDate).isValid()
        && !moment(filters.startDate).isAfter(moment(filters.endDate), 'day')

    const activeQuick = QUICK_RANGES.find((q) => {
        const r = rangeOf(q.days)
        return r.startDate === filters.startDate && r.endDate === filters.endDate
    })

    return (
        <div className={classes.root}>
            <div className={classes.headerBanner}>
                <div className={classes.headerIconWrap}>
                    <PhoneCallbackIcon style={{ fontSize: 26, color: '#fff' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                    <Typography className={classes.headerTitle}>Hold Revoke Calls</Typography>
                    <Typography className={classes.headerSubtitle}>
                        Leads placed on Hold carry the date the customer asked to be approached
                        again. This is what the sweep did when those dates came due.
                    </Typography>
                </div>
                {/* Says which period the figures cover, right next to them,
                    instead of leaving it to be inferred from the filter card. */}
                <div className={classes.headerDateChip}>
                    <DateRangeOutlinedIcon style={{ fontSize: 15 }} />
                    {rangeLabel}
                    {rangeDays && <span style={{ opacity: 0.75, fontWeight: 600 }}>{`· ${rangeDays}d`}</span>}
                </div>
                {/* Only once something is actually narrowed - a small number is
                    otherwise indistinguishable from a quiet month. */}
                {scopeLabel && (
                    <Tooltip title={scopeTitle}>
                        <div className={classes.headerDateChip}>
                            <PlaceOutlinedIcon style={{ fontSize: 15 }} />
                            {scopeLabel}
                        </div>
                    </Tooltip>
                )}
                <div className={classes.headerActions}>
                    <Tooltip
                        title={lastUpdated
                            ? `Last pulled at ${moment(lastUpdated).format('DD MMM YYYY, hh:mm:ss A')} — click to fetch the latest sweeps`
                            : 'Fetch the latest sweeps'}
                    >
                        {/* The span is required: a disabled button fires no
                            mouse events, so the tooltip would die mid-refresh. */}
                        <span style={{ display: 'inline-flex' }}>
                            <button
                                type="button"
                                className={classes.refreshButton}
                                onClick={() => loadOverview()}
                                disabled={loading}
                            >
                                <RefreshIcon
                                    className={loading ? classes.refreshSpinning : undefined}
                                    style={{ fontSize: 17 }}
                                />
                                {loading ? 'Refreshing…' : 'Refresh'}
                            </button>
                        </span>
                    </Tooltip>
                    {lastUpdated && (
                        <div className={classes.lastUpdatedText}>
                            <span className={classes.liveDot} />
                            {/* Relative beats "02:14:07 PM": it answers "is this
                                stale?" without the reader doing the arithmetic. */}
                            {loading ? 'Fetching latest…' : `Updated ${moment(lastUpdated).from(nowTick)}`}
                        </div>
                    )}
                </div>
            </div>

            {/* --------------------------------------------------- filters */}
            <Paper elevation={0} className={classes.filterPaper}>
                <div className={classes.filterHeaderRow}>
                    <div className={classes.filterHeader}>
                        <TuneIcon fontSize="small" />
                        Filters
                    </div>
                    {loading && (
                        <div className={classes.filterLoadingBadge}>
                            <CircularProgress size={12} thickness={5} style={{ color: ORANGE_DARK }} />
                            Fetching data, please wait…
                        </div>
                    )}
                </div>
                <div
                    className={classes.filterRow}
                    style={{ opacity: loading ? 0.55 : 1, pointerEvents: loading ? 'none' : 'auto' }}
                >
                    {/* One range, so one control. Clicking the label, the
                        arrow or the padding opens the start picker; clicking
                        either input opens that one's own. */}
                    <div
                        className={classes.dateGroup}
                        onClick={(event) => {
                            if (event.target.tagName !== 'INPUT') openPicker(startDateRef)
                        }}
                    >
                        <span className={classes.dateGroupLabel}>Period</span>
                        <TextField
                            type="date"
                            size="small"
                            className={classes.dateInput}
                            inputRef={startDateRef}
                            inputProps={{ max: filters.endDate || undefined, 'aria-label': 'Start date' }}
                            value={filters.startDate}
                            onClick={() => openPicker(startDateRef)}
                            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                            disabled={loading}
                        />
                        <span className={classes.dateDash}>→</span>
                        <TextField
                            type="date"
                            size="small"
                            className={classes.dateInput}
                            inputRef={endDateRef}
                            inputProps={{ min: filters.startDate || undefined, 'aria-label': 'End date' }}
                            value={filters.endDate}
                            onClick={() => openPicker(endDateRef)}
                            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                            disabled={loading}
                        />
                    </div>

                    <Autocomplete
                        multiple
                        size="small"
                        limitTags={1}
                        disabled={loading || optionsLoading}
                        value={filters.city}
                        onChange={handleCityChange}
                        options={cities}
                        getOptionLabel={(option) => option.name || ''}
                        getOptionSelected={(option, value) => option._id === value._id}
                        className={`${classes.field} ${classes.pickerField}`}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="City"
                                variant="outlined"
                                placeholder={filters.city.length ? '' : 'All cities'}
                            />
                        )}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        limitTags={1}
                        disabled={loading || optionsLoading}
                        value={filters.experienceCenterId}
                        onChange={(e, v) => setFilters({ ...filters, experienceCenterId: v })}
                        options={visibleCentres}
                        getOptionLabel={(option) => option.name || option.code || ''}
                        getOptionSelected={(option, value) => option._id === value._id}
                        className={`${classes.field} ${classes.centreField}`}
                        noOptionsText={filters.city.length
                            ? `No experience centre in ${filters.city.map((c) => c.name).join(', ')}`
                            : 'No experience centres found'}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Experience Center"
                                variant="outlined"
                                placeholder={filters.experienceCenterId.length
                                    ? ''
                                    : filters.city.length
                                        ? `All ${visibleCentres.length} in this city`
                                        : 'All centres'}
                            />
                        )}
                    />

                    <div className={classes.filterActions}>
                        {isDirty && !loading && (
                            <span className={classes.dirtyBadge}>
                                <span className={classes.dirtyDot} />
                                Not applied yet
                            </span>
                        )}
                        <AntButton
                            type="primary"
                            icon={<SearchIcon style={{ fontSize: 16, marginRight: 6 }} />}
                            onClick={applyFilters}
                            className={`${classes.applyButton} ${isDirty && !loading ? classes.applyButtonDirty : ''}`}
                            loading={loading}
                            disabled={loading || !datesValid}
                        >
                            {loading ? 'Loading...' : isDirty ? 'Apply changes' : 'Apply'}
                        </AntButton>
                        <AntButton
                            icon={<ClearIcon style={{ fontSize: 16, marginRight: 6 }} />}
                            onClick={clearFilters}
                            className={classes.clearButton}
                            disabled={loading}
                        >
                            Clear
                        </AntButton>
                    </div>
                </div>
                {/* v4's Chip has no `disabled` prop - it would be spread onto
                    the root div and React would warn about a non-boolean
                    attribute - so the row is greyed and unclickable instead. */}
                <div
                    className={classes.quickRanges}
                    style={{ opacity: loading ? 0.55 : 1, pointerEvents: loading ? 'none' : 'auto' }}
                >
                    <span className={classes.quickLabel}>Quick</span>
                    {QUICK_RANGES.map((q) => (
                        <Chip
                            key={q.label}
                            size="small"
                            label={q.label}
                            clickable
                            className={`${classes.quickChip} ${activeQuick === q ? classes.quickChipOn : ''}`}
                            onClick={() => setFilters({ ...filters, ...rangeOf(q.days) })}
                        />
                    ))}
                    {!datesValid && (
                        <span style={{ fontSize: 11.5, color: RED, fontWeight: 600, marginLeft: 6 }}>
                            The start date cannot be after the end date.
                        </span>
                    )}
                </div>

                {/* Always rendered so the card does not change height when it
                    appears - the row above would otherwise jump. The filters
                    are not live: nothing below moves until Apply runs, and
                    without saying so a changed dropdown over unchanged numbers
                    reads as a broken page. */}
                <div className={classes.applyHint}>
                    {isDirty && !loading ? (
                        <>
                            <SearchIcon style={{ fontSize: 13 }} />
                            Filters changed — press <b style={{ margin: '0 3px' }}>Apply</b> to update the dashboard.
                        </>
                    ) : (
                        <span style={{ color: '#a79d96' }}>
                            Pick your filters, then press Apply. Nothing below changes until you do.
                        </span>
                    )}
                </div>
            </Paper>

            <div className={classes.progressSlot}>
                {(loading || leadsLoading || runLoading) && <BrandLinearProgress />}
            </div>

            {(loading || leadsLoading || runLoading) && (
                <div className={classes.loadingBanner}>
                    <CircularProgress size={16} style={{ color: ORANGE_DARK }} />
                    <div>
                        <div className={classes.loadingBannerTitle}>
                            {runLoading ? 'Loading that run…' : leadsLoading ? 'Fetching leads…' : 'Loading the dashboard…'}
                        </div>
                        <div className={classes.loadingBannerHint}>
                            Counting Hold leads across the whole lead table — a moment.
                        </div>
                    </div>
                </div>
            )}

            {/* -------------------------------------------------- outcomes */}
            <Paper elevation={0} className={classes.card}>
                <div className={classes.sectionLabel}>
                    <CheckCircleIcon style={{ fontSize: 17 }} />
                    What the sweeps did
                    <span className={classes.sectionNote}>
                        {t.runs
                            ? `${t.runs} run${t.runs === 1 ? '' : 's'} · ${rangeLabel}`
                            : `no runs in ${rangeLabel}`}
                        {t.failedRuns ? ` · ${t.failedRuns} failed` : ''}
                    </span>
                </div>
                <div className={classes.tileGrid}>
                    {outcomeTiles.map((key) => {
                        const m = OUTCOME[key]
                        return (
                            <div key={key} className={classes.tile} style={{ background: m.bg, borderColor: `${m.color}22` }}>
                                <div className={classes.tileTop}>
                                    <m.Icon style={{ fontSize: 17, color: m.color }} />
                                    <span className={classes.tileLabel} style={{ color: m.color }}>{m.label}</span>
                                </div>
                                <div className={classes.tileValue}>
                                    {loading ? <Skeleton variant="text" width={46} /> : (t[key] ?? 0).toLocaleString()}
                                </div>
                                <div className={classes.tileHint}>{m.hint}</div>
                            </div>
                        )
                    })}
                </div>
            </Paper>

            {/* ------------------------------------------------------ runs */}
            <Paper elevation={0} className={classes.card}>
                <div className={classes.sectionLabel}>
                    <ScheduleIcon style={{ fontSize: 17 }} />
                    Runs
                </div>
                <TableContainer className={classes.tableWrap}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.headCell}>When</TableCell>
                                <TableCell className={classes.headCell}>Status</TableCell>
                                <TableCell className={classes.headCell}>Found</TableCell>
                                <TableCell className={classes.headCell}>Outcome</TableCell>
                                <TableCell className={classes.headCell}>Duration</TableCell>
                                <TableCell className={classes.headCell}>By</TableCell>
                                <TableCell className={classes.headCell} align="right">Details</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && [0, 1, 2].map((i) => (
                                <TableRow key={i}>
                                    {Array.from({ length: 7 }).map((_, c) => (
                                        <TableCell key={c}><Skeleton variant="text" /></TableCell>
                                    ))}
                                </TableRow>
                            ))}

                            {!loading && (overview?.runs || []).map((run) => {
                                const st = RUN_STATUS[run.status] || RUN_STATUS.processing
                                const total = BAR_PARTS.reduce((a, b) => a + (run[b.key] || 0), 0)
                                return (
                                    <TableRow key={run._id} className={classes.row}>
                                        <TableCell className={classes.bodyCell}>
                                            <div className={classes.whenMain}>{moment(run.createdAt).format('DD MMM, HH:mm')}</div>
                                            <div className={classes.whenSub}>{moment(run.createdAt).fromNow()}</div>
                                        </TableCell>
                                        <TableCell className={classes.bodyCell}>
                                            <Chip
                                                size="small"
                                                label={st.label}
                                                style={{ background: st.bg, color: st.color, fontWeight: 700, fontSize: 12 }}
                                            />
                                        </TableCell>
                                        <TableCell className={`${classes.bodyCell} ${classes.mono}`}>{run.found ?? 0}</TableCell>
                                        <TableCell className={classes.bodyCell} style={{ minWidth: 230 }}>
                                            <div className={classes.countStrip}>
                                                {BAR_PARTS.concat([{ key: 'gaveUp' }, { key: 'outsideWindow' }])
                                                    .filter((b) => run[b.key])
                                                    .map((b) => {
                                                        const m = OUTCOME[b.key]
                                                        return (
                                                            <Tooltip key={b.key} title={m.hint}>
                                                                <span className={classes.countPill} style={{ background: m.bg, color: m.color }}>
                                                                    {m.label} {run[b.key]}
                                                                </span>
                                                            </Tooltip>
                                                        )
                                                    })}
                                                {!total && !run.outsideWindow && !run.gaveUp && (
                                                    <span style={{ fontSize: 12, color: '#9aa6b1' }}>nothing was due</span>
                                                )}
                                            </div>
                                            {total > 0 && (
                                                <div className={classes.bar}>
                                                    {BAR_PARTS.map((b) => (run[b.key] ? (
                                                        <div key={b.key} style={{ width: `${(run[b.key] / total) * 100}%`, background: b.color }} />
                                                    ) : null))}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className={classes.bodyCell}>{fmtDuration(run.durationMs)}</TableCell>
                                        <TableCell className={classes.bodyCell}>{run.triggeredBy || '—'}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">
                                            <Tooltip title="Per-lead detail">
                                                <IconButton size="small" onClick={() => openRunDetails(run)}>
                                                    <VisibilityIcon style={{ fontSize: 17, color: ORANGE_DARK }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}

                            {!loading && !(overview?.runs || []).length && (
                                <TableRow>
                                    <TableCell colSpan={7} className={classes.emptyState}>
                                        <ScheduleIcon style={{ fontSize: 34, color: '#d9cdc5' }} />
                                        <Typography style={{ fontSize: 13.5, marginTop: 6 }}>
                                            No sweeps have run in this window yet.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* ----------------------------------------------------- leads */}
            <Paper elevation={0} className={classes.card}>
                <div className={classes.leadsToolbar}>
                    <div className={classes.sectionLabel} style={{ marginBottom: 0 }}>
                        <PauseCircleOutlineIcon style={{ fontSize: 17 }} />
                        Leads
                    </div>
                    <div className={classes.leadsToolbarActions}>
                        <TextField
                            size="small"
                            variant="outlined"
                            className={classes.searchField}
                            placeholder="Search lead no or customer…"
                            value={leadSearch}
                            onChange={(e) => {
                                setLeadSearch(e.target.value)
                                setLeadPage(0)
                            }}
                            inputProps={{ 'aria-label': 'Search leads by lead number or customer name' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ fontSize: 17, color: '#9aa6b1' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: leadSearch ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            aria-label="Clear search"
                                            onClick={() => { setLeadSearch(''); setLeadPage(0) }}
                                        >
                                            <ClearIcon style={{ fontSize: 15 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Tooltip title="Downloads one Excel file with three sheets — the leads in this tab (search applied), the sweep summary, and the run history.">
                            {/* A disabled MUI Button fires no mouse events, so
                                the tooltip needs a live element to sit on. */}
                            <span style={{ display: 'inline-flex' }}>
                                <Button
                                    size="small"
                                    className={classes.exportButton}
                                    startIcon={exporting
                                        ? <CircularProgress size={14} thickness={5} style={{ color: '#fff' }} />
                                        : <GetAppRoundedIcon style={{ fontSize: 17 }} />}
                                    onClick={downloadExcel}
                                    disabled={exporting || loading || leadsLoading || (!filteredLeads.length && !overview)}
                                >
                                    {exporting ? 'Preparing…' : 'Download Excel'}
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                </div>
                <Tabs
                    value={bucket}
                    onChange={(e, v) => setBucket(v)}
                    className={classes.tabs}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {BUCKETS.map((b) => (
                        <Tab
                            key={b.value}
                            className={classes.tab}
                            value={b.value}
                            label={`${b.label} (${p[b.countKey] ?? 0})`}
                        />
                    ))}
                </Tabs>

                {/* Always rendered, so turning a search on and off does not
                    shuffle the table up and down the page. */}
                <div className={classes.resultMeta}>
                    {leadQuery ? (
                        <>
                            <span className={classes.matchPill}>
                                <SearchIcon style={{ fontSize: 13 }} />
                                {filteredLeads.length} of {leads.length} match “{leadSearch.trim()}”
                            </span>
                            <button
                                type="button"
                                className={classes.clearSearchLink}
                                onClick={() => { setLeadSearch(''); setLeadPage(0) }}
                            >
                                Clear search
                            </button>
                        </>
                    ) : (
                        <span>
                            {leads.length
                                ? `Showing ${leads.length} lead${leads.length === 1 ? '' : 's'} in this tab`
                                : ''}
                        </span>
                    )}
                    {/* The server caps the list. Without saying so, a search
                        that finds nothing reads as "this lead does not exist". */}
                    {leadsTotal > leads.length && (
                        <span className={classes.capNote}>
                            · search covers the {leads.length} loaded of {leadsTotal} in this bucket
                        </span>
                    )}
                </div>

                {/* maxHeight + stickyHeader: 500 leads scroll under a header
                    that stays put, instead of scrolling the whole page away
                    from the tabs. */}
                <TableContainer className={classes.scrollWrap} style={{ maxHeight: 460 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.headCell} style={{ width: 56 }}>Sl. No.</TableCell>
                                <TableCell className={classes.headCell}>Lead No</TableCell>
                                <TableCell className={classes.headCell}>Customer</TableCell>
                                <TableCell className={classes.headCell}>Experience Centre</TableCell>
                                <TableCell className={classes.headCell}>Revoke date</TableCell>
                                <TableCell className={classes.headCell}>Called at</TableCell>
                                <TableCell className={classes.headCell}>Attempts</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {leadsLoading && [0, 1, 2].map((i) => (
                                <TableRow key={i}>
                                    {Array.from({ length: LEAD_COLUMNS }).map((_, c) => (
                                        <TableCell key={c}><Skeleton variant="text" /></TableCell>
                                    ))}
                                </TableRow>
                            ))}

                            {!leadsLoading && pagedLeads.map((l, i) => (
                                <TableRow key={l._id} className={classes.row}>
                                    {/* Continues across pages - restarting at 1
                                        on page 2 makes the number useless for
                                        "the 37th one down". */}
                                    <TableCell className={`${classes.bodyCell} ${classes.mono}`} style={{ color: '#9aa6b1' }}>
                                        {safeLeadPage * leadRowsPerPage + i + 1}
                                    </TableCell>
                                    <TableCell className={classes.bodyCell}>
                                        {l._id ? (
                                            <Tooltip title="Open this lead's page in a new tab">
                                                <span
                                                    className={classes.leadLink}
                                                    onClick={() => window.open(`/project-logs/${l._id}`, '_blank', 'noopener,noreferrer')}
                                                >
                                                    {l.leadNo || '—'}
                                                    <OpenInNewIcon style={{ fontSize: 11 }} />
                                                </span>
                                            </Tooltip>
                                        ) : (l.leadNo || '—')}
                                    </TableCell>
                                    <TableCell className={classes.bodyCell} style={{ maxWidth: 230 }}>
                                        <div className={classes.customerCell}>
                                            <span
                                                className={classes.avatar}
                                                style={avatarToneOf(l.customerName)}
                                                aria-hidden="true"
                                            >
                                                {initialsOf(l.customerName)}
                                            </span>
                                            <span style={{ minWidth: 0 }}>
                                                <div className={classes.customerName} title={l.customerName || ''}>
                                                    {l.customerName || 'Unknown'}
                                                </div>
                                                {/* currentStage already comes back from /leads and
                                                    nothing was showing it - where the lead actually
                                                    sits is the first thing asked about a hold. */}
                                                {l.currentStage && (
                                                    <div className={classes.customerStage} title={l.currentStage}>
                                                        {l.currentStage}
                                                    </div>
                                                )}
                                            </span>
                                        </div>
                                    </TableCell>
                                    {/* Centre over city, one column. The city
                                        is never read without the centre beside
                                        it, and a column of its own only made
                                        the table wider. */}
                                    <TableCell className={classes.bodyCell}>
                                        <div className={classes.whenMain}>{l.experienceCenter || '—'}</div>
                                        <div className={classes.cityLine}>
                                            {l.city ? (
                                                <>
                                                    <PlaceOutlinedIcon style={{ fontSize: 11 }} />
                                                    {l.city}
                                                </>
                                            ) : 'No city on record'}
                                        </div>
                                    </TableCell>
                                    <TableCell className={classes.bodyCell}>
                                        {l.holdRevokeDate ? (
                                            <>
                                                <div className={classes.whenMain}>{moment(l.holdRevokeDate).format('DD MMM YYYY')}</div>
                                                <div className={classes.whenSub}>{moment(l.holdRevokeDate).fromNow()}</div>
                                            </>
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell className={classes.bodyCell}>
                                        {l.holdRevokeCalledAt ? moment(l.holdRevokeCalledAt).format('DD MMM, HH:mm') : '—'}
                                    </TableCell>
                                    <TableCell className={classes.bodyCell}>
                                        <span
                                            className={classes.countPill}
                                            style={l.attempts >= 3
                                                ? { background: AMBER_LIGHT, color: AMBER }
                                                : { background: GREY_LIGHT, color: GREY }}
                                        >
                                            {l.attempts}
                                        </span>
                                    </TableCell>
                                </TableRow>
                            ))}

                            {/* Two different nothings. "Nothing in this bucket"
                                over a table emptied by a search is a lie, and
                                the fix for it is one click away. */}
                            {!leadsLoading && !filteredLeads.length && (
                                <TableRow>
                                    <TableCell colSpan={LEAD_COLUMNS} className={classes.emptyState}>
                                        {leadQuery ? (
                                            <>
                                                <SearchIcon style={{ fontSize: 34, color: '#d9cdc5' }} />
                                                <Typography style={{ fontSize: 13.5, marginTop: 6 }}>
                                                    No lead number or customer here matches “{leadSearch.trim()}”.
                                                </Typography>
                                                <Typography style={{ fontSize: 12, marginTop: 4, color: '#a79d96' }}>
                                                    Try another tab, or widen the date and city filters above.
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    onClick={() => { setLeadSearch(''); setLeadPage(0) }}
                                                    style={{ textTransform: 'none', fontWeight: 700, marginTop: 8, color: BLUE }}
                                                >
                                                    Clear search
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircleIcon style={{ fontSize: 34, color: '#d9cdc5' }} />
                                                <Typography style={{ fontSize: 13.5, marginTop: 6 }}>
                                                    Nothing in this bucket.
                                                </Typography>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Rendered whenever there is anything at all, not only past
                    one page - otherwise the rows-per-page control disappears
                    exactly when someone wants to raise it. */}
                {!leadsLoading && filteredLeads.length > 0 && (
                    <TablePagination
                        className={classes.pagination}
                        component="div"
                        count={filteredLeads.length}
                        page={safeLeadPage}
                        onChangePage={(event, pg) => setLeadPage(pg)}
                        rowsPerPage={leadRowsPerPage}
                        onChangeRowsPerPage={(event) => {
                            setLeadRowsPerPage(parseInt(event.target.value, 10))
                            setLeadPage(0)
                        }}
                        rowsPerPageOptions={LEAD_ROWS_OPTIONS}
                        labelDisplayedRows={({ from, to, count }) => {
                            if (leadQuery) return `${from}-${to} of ${count} matching`
                            // The server caps the list; say so rather than
                            // letting the cap read as the whole answer.
                            return leadsTotal > leads.length
                                ? `${from}-${to} of ${count} shown · ${leadsTotal} in this bucket`
                                : `${from}-${to} of ${count}`
                        }}
                    />
                )}
            </Paper>

            {/* --------------------------------------------- run details */}
            <Dialog
                open={Boolean(openRun)}
                onClose={() => setOpenRun(null)}
                classes={{ paper: classes.dialogPaper }}
                TransitionComponent={Grow}
            >
                <DialogTitle disableTypography className={classes.dialogTitle}>
                    Run detail
                    {openRun?.createdAt ? ` — ${moment(openRun.createdAt).format('DD MMM YYYY, HH:mm')}` : ''}
                </DialogTitle>
                <DialogContent>
                    {openRun?.loading && <Skeleton variant="rect" height={180} style={{ borderRadius: 10 }} />}

                    {openRun && !openRun.loading && (
                        <>
                            {openRun.errorMessage && (
                                <div className={classes.errorBox}>{openRun.errorMessage}</div>
                            )}

                            <div className={classes.countStrip} style={{ marginBottom: 14 }}>
                                {Object.keys(OUTCOME)
                                    .filter((k) => openRun[k])
                                    .map((k) => (
                                        <span key={k} className={classes.countPill} style={{ background: OUTCOME[k].bg, color: OUTCOME[k].color }}>
                                            {OUTCOME[k].label} {openRun[k]}
                                        </span>
                                    ))}
                                <span className={classes.countPill} style={{ background: GREY_LIGHT, color: GREY }}>
                                    Duration {fmtDuration(openRun.durationMs)}
                                </span>
                            </div>

                            <Divider style={{ marginBottom: 12 }} />

                            <TableContainer className={classes.scrollWrap} style={{ maxHeight: 360 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className={classes.headCell}>Lead No</TableCell>
                                            <TableCell className={classes.headCell}>Outcome</TableCell>
                                            <TableCell className={classes.headCell}>Note</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(openRun.details || []).map((d, i) => {
                                            const m = OUTCOME[d.status] || OUTCOME.error
                                            return (
                                                <TableRow key={i} className={classes.row}>
                                                    <TableCell className={classes.bodyCell}>
                                                        {d.leadId ? (
                                                            <Tooltip title="Open this lead's page in a new tab">
                                                                <span
                                                                    className={classes.leadLink}
                                                                    onClick={() => window.open(`/project-logs/${d.leadId}`, '_blank', 'noopener,noreferrer')}
                                                                >
                                                                    {/* The per-lead rows carry a leadId but not
                                                                        always a number, so the last 6 of the id
                                                                        stays as the fallback label. */}
                                                                    {d.leadNo || d.leadId.slice(-6)}
                                                                    <OpenInNewIcon style={{ fontSize: 11 }} />
                                                                </span>
                                                            </Tooltip>
                                                        ) : (d.leadNo || '—')}
                                                    </TableCell>
                                                    <TableCell className={classes.bodyCell}>
                                                        <Chip
                                                            size="small"
                                                            icon={<m.Icon style={{ color: m.color, fontSize: 15 }} />}
                                                            label={m.label}
                                                            style={{ background: m.bg, color: m.color, fontWeight: 700, fontSize: 11.5 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell className={classes.bodyCell} style={{ fontSize: 12.5, color: '#5b4b42' }}>
                                                        {d.note || d.reason || d.error
                                                            || (d.attempts ? `Attempt ${d.attempts} of 3` : '—')}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}

                                        {!(openRun.details || []).length && (
                                            <TableRow>
                                                <TableCell colSpan={3} className={classes.emptyState}>
                                                    No leads were touched by this run.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '8px 20px 18px' }}>
                    <Button onClick={() => setOpenRun(null)} style={{ textTransform: 'none', fontWeight: 600, borderRadius: 20 }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default connect(null, { setSnackInfo })(HoldRevokeMonitor)
